import { useState } from 'react';
import { Megaphone, ShieldAlert, Loader2, Send, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  useBroadcastBulkMutation,
  useBroadcastSendMutation,
} from '@/client/api/messaging/messaging.mutation';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';

const AccessDenied = ({ code }: { code: string }) => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      This action requires the{' '}
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">{code}</code> permission.
    </p>
  </div>
);

const MessagingBroadcastPage = () => {
  useBreadcrumbs([
    { label: 'Communications', href: '/admin/communications/messaging' },
    { label: 'Messaging' },
    { label: 'Broadcast' },
  ]);

  const viewPerm = useHasPermission('message.view');
  const broadcastPerm = useHasPermission('message.broadcast');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canBroadcast = broadcastPerm.isSuperAdmin || broadcastPerm.hasPermission;
  const permLoading = viewPerm.isLoading || broadcastPerm.isLoading;

  const send = useBroadcastSendMutation();
  const bulk = useBroadcastBulkMutation();

  const [mode, setMode] = useState<'single' | 'bulk'>('single');
  const [targetType, setTargetType] = useState<'uid' | 'oid'>('uid');
  const [targetId, setTargetId] = useState('');
  const [entity, setEntity] = useState('');
  const [priority, setPriority] = useState('normal');
  const [message, setMessage] = useState('');
  const [bulkTargets, setBulkTargets] = useState('');

  if (permLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-3xl space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!canView && !canBroadcast) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-3xl">
          <AccessDenied code="message.view" />
        </div>
      </div>
    );
  }

  if (!canBroadcast) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-3xl">
          <AccessDenied code="message.broadcast" />
        </div>
      </div>
    );
  }

  const handleSingle = async () => {
    const id = Number(targetId.trim());
    if (!Number.isFinite(id) || id <= 0) {
      toast.error('Enter a valid UID or OID', { richColors: true });
      return;
    }
    if (!message.trim()) {
      toast.error('Message is required', { richColors: true });
      return;
    }
    const entityNum = entity.trim() ? Number(entity.trim()) : undefined;
    try {
      const result = await send.mutateAsync({
        body: {
          message: message.trim(),
          priority,
          ...(targetType === 'uid' ? { uid: id } : { oid: id }),
          ...(entityNum != null && Number.isFinite(entityNum) ? { entity: entityNum } : {}),
        },
        idempotencyKey: generateIdempotencyKey(),
      });
      toast.success(
        `Sent — ${result.conversation_key || result.id || 'ok'}`,
        { richColors: true },
      );
      setMessage('');
    } catch (error: any) {
      toast.error(error?.message || 'Send failed', { richColors: true });
    }
  };

  const handleBulk = async () => {
    if (!message.trim()) {
      toast.error('Message is required', { richColors: true });
      return;
    }
    const targets = bulkTargets
      .split(/[\n,]+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const lower = line.toLowerCase();
        if (lower.startsWith('oid:')) {
          return { oid: Number(line.slice(4).trim()) };
        }
        if (lower.startsWith('uid:')) {
          return { uid: Number(line.slice(4).trim()) };
        }
        return { uid: Number(line) };
      })
      .filter((t) =>
        (t.uid != null && Number.isFinite(t.uid) && t.uid > 0) ||
        (t.oid != null && Number.isFinite(t.oid) && t.oid > 0),
      );

    if (targets.length === 0) {
      toast.error('Add at least one target (uid or oid:N)', { richColors: true });
      return;
    }
    if (targets.length > 5000) {
      toast.error('Max 5000 targets per bulk send', { richColors: true });
      return;
    }

    try {
      const result = await bulk.mutateAsync({
        body: { message: message.trim(), targets, priority },
        idempotencyKey: generateIdempotencyKey(),
      });
      toast.success(`Broadcast sent to ${result.sent ?? targets.length} recipients`, {
        richColors: true,
      });
      setMessage('');
      setBulkTargets('');
    } catch (error: any) {
      toast.error(error?.message || 'Bulk broadcast failed', { richColors: true });
    }
  };

  const busy = send.isPending || bulk.isPending;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-3xl space-y-6">
        <div>
          <div className="mb-1 flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
              <Megaphone className="h-4 w-4 text-white" />
            </div>
            <h1 className="text-2xl font-black text-gray-900">Broadcast</h1>
          </div>
          <p className="text-sm text-gray-500">
            Send platform messages to users or orgs. They persist as{' '}
            <code className="text-xs">is_platform</code> in the canonical store.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setMode('single')}
            className={`rounded-md border px-3 py-1.5 text-sm ${
              mode === 'single'
                ? 'border-blue-200 bg-blue-50 text-blue-800'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            Single recipient
          </button>
          <button
            type="button"
            onClick={() => setMode('bulk')}
            className={`rounded-md border px-3 py-1.5 text-sm ${
              mode === 'bulk'
                ? 'border-blue-200 bg-blue-50 text-blue-800'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            Bulk
          </button>
        </div>

        <div className="space-y-4 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
          {mode === 'single' ? (
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-xs text-gray-500">Target type</label>
                <select
                  value={targetType}
                  onChange={(e) => setTargetType(e.target.value as 'uid' | 'oid')}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="uid">User (UID)</option>
                  <option value="oid">Organization (OID)</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs text-gray-500">
                  {targetType === 'uid' ? 'UID' : 'OID'}
                </label>
                <input
                  type="number"
                  value={targetId}
                  onChange={(e) => setTargetId(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="101"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-gray-500">Entity (optional)</label>
                <input
                  type="number"
                  value={entity}
                  onChange={(e) => setEntity(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="42"
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="mb-1 block text-xs text-gray-500">
                Targets (one per line: UID, or <code>oid:9</code> / <code>uid:101</code>)
              </label>
              <textarea
                value={bulkTargets}
                onChange={(e) => setBulkTargets(e.target.value)}
                rows={6}
                className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={'101\n243\noid:9\nuid:5502'}
              />
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs text-gray-500">Priority</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="w-full max-w-xs rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs text-gray-500">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Your listing was approved…"
            />
          </div>

          <p className="rounded-md border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Delivery is best-effort over sockets — recipients see the message on their next
            history fetch or reconnect. Pair with a push notification for time-critical
            announcements.
          </p>

          <div className="flex justify-end">
            <Button
              type="button"
              disabled={busy}
              onClick={mode === 'single' ? handleSingle : handleBulk}
              className="gap-2"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : mode === 'single' ? (
                <Send className="h-4 w-4" />
              ) : (
                <Users className="h-4 w-4" />
              )}
              {mode === 'single' ? 'Send' : 'Broadcast'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagingBroadcastPage;
